import frFR from "date-fns/locale/fr"
import { format } from "date-fns"
import { DateTime } from "luxon"
import { createGlobalStyle } from "styled-components"
import type { DateRange, RangeType } from "rsuite/esm/DateRangePicker"
import { ReactNode, SyntheticEvent } from "react"
import { colors } from "../styles/design.config"
import { Control, FieldValues } from "react-hook-form"

export const formatTimeFRstring = (dateString: string) => {
  if (!dateString) return ""
  return new Date(dateString).toLocaleTimeString("fr")
}
export const formatDateFRstring = (dateString: string) => {
  if (!dateString) return "Aucune"
  return new Date(dateString).toLocaleDateString("fr")
}

export const formatDateDate = (dateString: Date) => {
  return new Date(dateString).toLocaleDateString()
}

export const formatDateFRdate = (dateString: Date) => {
  if (!dateString) return "Aucune"
  return new Date(dateString).toLocaleDateString("fr")
}

export const dateToFullDocumentFormat = (date: Date | null | undefined) => {
  if (!date) return null
  return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd")
}

export function formatDateNumbering(
  numberingDateFormat: string,
  dateString: Date
): string {
  if (!numberingDateFormat) {
    return ""
  }
  const englishFormat = numberingDateFormat
    .replace(/A/g, "y")
    .replace(/J/g, "d")

  return format(dateString, englishFormat)
}

export function formatTimeHHhMM(dateString: Date): string {
  if (!dateString) return ""
  const d = new Date(dateString)
  const hh = String(d.getUTCHours()).padStart(2, "0")
  const mm = String(d.getUTCMinutes()).padStart(2, "0")
  return `${hh}h${mm}`
}

export function formatDateDatePicker(date: Date) {
  const year = String(date.getFullYear()).padStart(4, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export const monthsArray = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
]

export function formatDateFrWithSeconds(dateString: string) {
  const date = new Date(dateString)
  const datePattern = new Intl.DateTimeFormat("fr", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
  return datePattern.format(date)
}

export function getDueDate({
  billDate,
  paymentPeriod,
}: {
  billDate: string
  paymentPeriod: number
}) {
  return DateTime.fromFormat(billDate, "yyyy-MM-dd")
    .plus({
      day: paymentPeriod,
    })
    .toFormat("yyyy-MM-dd")
}

export const formatToFirstDayOfMonth = (date: Date) => {
  const dt = DateTime.fromJSDate(date)
  const firstDayOfMonth = dt.startOf("month")
  return firstDayOfMonth.toFormat("yyyy-MM-dd")
}

export const formatToFirstDayOfNextMonth = (date: Date) => {
  const dt = DateTime.fromJSDate(date)
  const firstDayOfMonth = dt.plus({ month: 1 }).startOf("month")
  return firstDayOfMonth.toFormat("yyyy-MM-dd")
}

export const getDatePreviousMonth = (date: Date) => {
  const dt = DateTime.fromJSDate(date)
  const previous = dt.minus({ month: 1 })
  return previous.toJSDate()
}

export const getMonthString = (date: Date) => {
  return monthsArray[date.getMonth()].label
}

export const optionsConfig = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
}

const Calendar = {
  sunday: "Di",
  monday: "Lu",
  tuesday: "Ma",
  wednesday: "Me",
  thursday: "Je",
  friday: "Ve",
  saturday: "Sa",
  ok: "Appliquer",
  today: "Aujourd'hui",
  yesterday: "Hier",
  hours: "Heures",
  minutes: "Minutes",
  seconds: "Secondes",
  formattedMonthPattern: "MMMM yyyy",
  formattedDayPattern: "dd MMMM yyyy",
  dateLocale: frFR,
}

export const locale = {
  Calendar,
  DatePicker: {
    ...Calendar,
  },
  DateRangePicker: {
    ...Calendar,
    last7Days: "7 derniers jours",
  },
}

export interface InputDateProps {
  value: [Date, Date] | null
  onChange: (
    value: DateRange | null,
    event: SyntheticEvent<Element, Event>
  ) => void
  onClose?: () => void
  onEntered?: () => void
  onExiting?: () => void
  minimum?: string
  maximum?: string
  placement?: Placement
  editable?: boolean
  cleanable?: boolean
  closable?: boolean
  predefinedRanges?: RangeType[]
  placeholder?: string
  hideBoxShadow?: boolean
  textAlign?: "left" | "center" | "right" | "auto"
  fontWeight?: string
  fontSize?: string
  fontFamily?: string
}

export interface InputDatePickerProps {
  name?: string
  label?: string
  value: string | null
  onChange: (date: string | null, event: SyntheticEvent<Element, Event>) => void
  onClean?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onOpen?: () => void
  onEntered?: () => void
  onExiting?: () => void
  minimum?: string
  maximum?: string
  cleanable?: boolean
  disabled?: boolean
  editable?: boolean
  placement?: Placement
  placeholder?: string
  textAlign?: "left" | "center" | "right" | "auto"
  messageTooltipDisabled?: string
  width?: string
  prefix?: ReactNode
  fontSize?: string
  height?: string
  maxHeight?: string
  showBorder?: boolean
  borderRadius?: string
  showError?: boolean
  control?: Control<FieldValues>
}

export type Placement =
  | "bottomStart"
  | "bottomEnd"
  | "topStart"
  | "topEnd"
  | "leftStart"
  | "leftEnd"
  | "rightStart"
  | "rightEnd"
  | "auto"
  | "autoVerticalStart"
  | "autoVerticalEnd"
  | "autoHorizontalStart"
  | "autoHorizontalEnd"

/* stylelint-disable no-descending-specificity */
export const StyledCalendarGlobal = createGlobalStyle`
  .rs-picker-error > .rs-picker-input-group {
    border-color: transparent!important;
    outline: none!important;
  }

  .rs-input-group:not(.rs-input-group-disabled):hover {
    border-color: transparent!important;
  }

  .rs-input-group:not(.rs-input-group-disabled):focus-within {
    border-color: transparent!important;
    outline: none!important;
  }

  .rs-picker-error>.rs-picker-input-group:focus-within{
    border-color: transparent!important;
    outline: none!important;
  }

  .rs-input-group:not(.rs-input-group-disabled) {
    border-color: transparent!important;
    outline: none!important;
  }

  .rs-input {
    padding-left: 2rem;
  }

  .rs-calendar > .rs-calendar-header > div.rs-calendar-header-month-toolbar {
    width: 100%;
  }

  .rs-input-group.rs-input-group-inside .rs-input-group-addon {
    padding: 0;
  }

  .rs-picker-popup.rs-picker-popup-daterange .rs-calendar:first-child {
    border-right: none;
  }

  .rs-picker-daterange-predefined {
    height: 328.8px;
  }

  .rs-picker-daterange-predefined > .rs-stack-item {
    width: 100%;
  }

  .rs-picker-daterange-predefined > .rs-stack-item > button {
    width: 100%;
  }

  .rs-picker-popup {
    z-index: 5000;
  }
`

export const StyledDatePickerGlobal = createGlobalStyle`
  .rs-picker-date.rs-picker-cleanable.rs-picker-has-value .rs-picker-toggle,
  .rs-picker-daterange.rs-picker-cleanable.rs-picker-has-value
    .rs-picker-toggle {
    border: none;
  }
  .rs-picker-toggle-wrapper {
    width: 100%;
    height: 100%;
    animation-fill-mode: backwards;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .rs-picker-date .rs-picker-toggle.rs-btn .rs-picker-toggle-caret {
    top: 7px;
  }
  .rs-picker-date .rs-picker-toggle.rs-btn .rs-picker-toggle-clean {
    top: 4px;
  }

  .rs-picker-has-value .rs-btn .rs-picker-toggle-value,
  .rs-picker-has-value .rs-picker-toggle .rs-picker-toggle-value {
    color: ${colors.navy};
  }

  /* Ensure popup appears above modals */
  .rs-picker-popup {
    z-index: 100000 !important;
  }
`

export const StyledMonthPickerGlobal = createGlobalStyle`
  .rs-picker-date.rs-picker-cleanable.rs-picker-has-value .rs-picker-toggle,
  .rs-picker-daterange.rs-picker-cleanable.rs-picker-has-value
    .rs-picker-toggle {
    border: none;
  }
  .rs-picker-toggle-wrapper {
    width: 100%;
    height: 100%;
    animation-fill-mode: backwards;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .rs-picker-date .rs-picker-toggle.rs-btn .rs-picker-toggle-caret {
    top: 7px;
  }
  .rs-picker-date .rs-picker-toggle.rs-btn .rs-picker-toggle-clean {
    top: 4px;
  }

  .rs-picker-has-value .rs-btn .rs-picker-toggle-value,
  .rs-picker-has-value .rs-picker-toggle .rs-picker-toggle-value {
    color: ${colors.navy};
  }

  .rs-calendar-month-dropdown-row-wrapper {
    overflow-x: hidden!important;
  }

  .rs-input-group-addon {
    display: none;
  }

  .rs-input-group {
    border: none;
  }

  .rs-input {
    padding-left: 1rem;
  }
`

export const StyledDatePickerPopupGlobal = createGlobalStyle<{
  toastMessage: string
}>`

  .rs-picker-popup .rs-calendar {
    overflow: visible;
  }

  .rs-picker-popup.rs-picker-popup-date {
    overflow: visible;
  }

  .rs-calendar-table-cell-disabled:hover:before {
    z-index: 100001;
    content: "${({ toastMessage }) => toastMessage}";
    position: absolute;
    border: 1px solid #031D42;
    background-color: white;
    color: #031D42;
    border-radius: 1rem;
    padding: 1rem;
    height: auto;
    width: 18rem;
    font-size: 1.75rem;
    display: flex;
    align-items: center;
  }
`

type DateFormat = "yyyy-MM-dd" | "dd/MM/yyyy"

/**
 * Compare two dates
 * @param date1 first date
 * @param dateFormat1 format of the first date
 * @param date2 second date
 * @param dateFormat2 format of the second date
 * @returns 0 if dates are equal, 1 if date1 is greater than date2, -1 if date1 is less than date2
 */
export const compareDates = (
  date1: string,
  dateFormat1: DateFormat,
  date2: string,
  dateFormat2: DateFormat
) => {
  const d1 = DateTime.fromFormat(date1, dateFormat1).toJSDate()
  const d2 = DateTime.fromFormat(date2, dateFormat2).toJSDate()

  d1.setHours(0, 0, 0, 0)
  d2.setHours(0, 0, 0, 0)

  if (d1.getTime() === d2.getTime()) return 0
  return d1 > d2 ? 1 : -1
}

export const dateMatchesUserInput = (search: string, isoDate: string) => {
  const [targetYear, targetMonth, targetDay] = isoDate
    .split("-")
    .map((s) => parseInt(s, 10))
  if (!targetYear || !targetMonth || !targetDay) {
    return false
  }
  const targetShortYear = targetYear % 100

  const trimmedSearch = search.trim()
  let partsStr = trimmedSearch.match(/\d+/g) || []
  let parts = partsStr.map((s) => parseInt(s, 10))

  if (parts.some(isNaN)) return false

  if (parts.length === 2) {
    const [p1, p2] = parts
    if (p1 === targetDay && p2 === targetMonth) return true
    if (p1 === targetMonth && p2 === targetDay) return true
    if (p1 === targetMonth && p2 === targetShortYear) return true
    if (p1 === targetShortYear && p2 === targetMonth) return true
  }

  if (parts.length === 3) {
    const [p1, p2, p3] = parts
    if (p1 === targetDay && p2 === targetMonth && p3 === targetShortYear)
      return true
    if (p1 === targetDay && p2 === targetMonth && p3 === targetYear) return true
    if (p1 === targetShortYear && p2 === targetMonth && p3 === targetDay)
      return true
    if (p1 === targetYear && p2 === targetMonth && p3 === targetDay) return true
  }

  if (
    parts.length === 1 ||
    (partsStr[0] && trimmedSearch.indexOf(partsStr[0]) === 0)
  ) {
    const digitsOnly = trimmedSearch.replace(/\D/g, "")
    const len = digitsOnly.length

    if (len === 2) {
      const p1 = parseInt(digitsOnly.slice(0, 2), 10)
      if (p1 === targetDay || p1 === targetMonth) return true
    }

    if (len === 4) {
      const p1 = parseInt(digitsOnly.slice(0, 2), 10)
      const p2 = parseInt(digitsOnly.slice(2, 4), 10)
      if (p1 === targetDay && p2 === targetMonth) return true
      if (p1 === targetMonth && p2 === targetDay) return true
    }

    if (len === 6) {
      const p1 = parseInt(digitsOnly.slice(0, 2), 10)
      const p2 = parseInt(digitsOnly.slice(2, 4), 10)
      const p3 = parseInt(digitsOnly.slice(4, 6), 10)
      if (p1 === targetDay && p2 === targetMonth && p3 === targetShortYear)
        return true
      if (p1 === targetShortYear && p2 === targetMonth && p3 === targetDay)
        return true
    }

    if (len === 8) {
      const p1 = parseInt(digitsOnly.slice(0, 2), 10)
      const p2 = parseInt(digitsOnly.slice(2, 4), 10)
      const p3 = parseInt(digitsOnly.slice(4, 8), 10)
      if (p1 === targetDay && p2 === targetMonth && p3 === targetYear)
        return true

      const y = parseInt(digitsOnly.slice(0, 4), 10)
      const m = parseInt(digitsOnly.slice(4, 6), 10)
      const d = parseInt(digitsOnly.slice(6, 8), 10)
      if (y === targetYear && m === targetMonth && d === targetDay) return true
    }
  }

  return false
}
